import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, CircleHelp } from "lucide-react";

export function RequiredLabel({ children }) {
  return (
    <label className="teacher-mocks-field-label">
      {children}
      <span aria-hidden="true">*</span>
    </label>
  );
}

export function FieldError({ id, children }) {
  return (
    <p id={id} className="teacher-mocks-field-error">
      {children}
    </p>
  );
}

export function InfoTooltipButton({ label }) {
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  function showTooltip() {
    timerRef.current = setTimeout(() => {
      if (btnRef.current) {
        const r = btnRef.current.getBoundingClientRect();
        const tooltipWidth = Math.min(240, window.innerWidth - 24);
        const minLeft = 12 + tooltipWidth / 2;
        const maxLeft = window.innerWidth - 12 - tooltipWidth / 2;
        const left = Math.min(Math.max(r.left + r.width / 2, minLeft), maxLeft);
        setPos({ top: r.top, left });
      }
    }, 300);
  }

  function hideTooltip() {
    clearTimeout(timerRef.current);
    setPos(null);
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className="tprof-info-tooltip-trigger"
        aria-label={label}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        <CircleHelp size={14} aria-hidden="true" />
      </button>
      {pos && (
        <div
          className="tprof-tooltip tprof-info-tooltip"
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            transform: "translate(-50%, calc(-100% - 8px))",
            zIndex: 1200,
            pointerEvents: "none",
          }}
        >
          {label}
        </div>
      )}
    </>
  );
}

export function MetricCard({ label, value, description, tooltip, onClick, isActive }) {
  const classes = [
    "tprof-alert-card",
    onClick ? "tprof-alert-card--clickable" : "",
    isActive ? "tprof-alert-card--active" : "",
  ].filter(Boolean).join(" ");

  return (
    <div
      className={classes}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") onClick(); } : undefined}
    >
      <div className="tprof-alert-header">
        <div className="tprof-alert-label">{label}</div>
        <InfoTooltipButton label={tooltip} />
      </div>
      <div className="tprof-alert-value">{value}</div>
      <div className="tprof-alert-desc">{description}</div>
      {onClick && (
        <div className="tprof-alert-action">
          {isActive ? "Filtro ativo · clique para remover" : "Filtrar >"}
        </div>
      )}
    </div>
  );
}

export function TooltipButton({ label, className, disabled, onClick, children }) {
  const [pos, setPos] = useState(null);
  const btnRef = useRef(null);
  const timerRef = useRef(null);
  const hideTimerRef = useRef(null);
  const isLongPressRef = useRef(false);

  useEffect(() => () => {
    clearTimeout(timerRef.current);
    clearTimeout(hideTimerRef.current);
  }, []);

  function computePos() {
    if (!btnRef.current) return null;
    const r = btnRef.current.getBoundingClientRect();
    return { top: r.top, left: r.left + r.width / 2 };
  }

  function handleMouseEnter() {
    timerRef.current = setTimeout(() => setPos(computePos()), 300);
  }

  function handleMouseLeave() {
    clearTimeout(timerRef.current);
    setPos(null);
  }

  function handleTouchStart() {
    isLongPressRef.current = false;
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      isLongPressRef.current = true;
      setPos(computePos());
      hideTimerRef.current = setTimeout(() => {
        setPos(null);
        isLongPressRef.current = false;
      }, 1500);
    }, 500);
  }

  function handleTouchEnd(e) {
    clearTimeout(timerRef.current);
    if (isLongPressRef.current) {
      e.preventDefault();
      isLongPressRef.current = false;
    }
  }

  function handleTouchCancel() {
    clearTimeout(timerRef.current);
    clearTimeout(hideTimerRef.current);
    isLongPressRef.current = false;
    setPos(null);
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={className}
        aria-label={label}
        disabled={disabled}
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchCancel}
        onTouchCancel={handleTouchCancel}
      >
        {children}
      </button>
      {pos && (
        <div
          className="tprof-tooltip"
          style={{
            position: "fixed",
            top: pos.top,
            left: pos.left,
            transform: "translate(-50%, calc(-100% - 8px))",
            zIndex: 1200,
            pointerEvents: "none",
          }}
        >
          {label}
        </div>
      )}
    </>
  );
}

export function Badge({ children, variant = "default" }) {
  return <span className={`teacher-mocks-manager-badge ${variant}`}>{children}</span>;
}

export function CompactAdvancedDropdown({ label, options, selectedValues, onChange, placeholder = "Todas", isLoading = false }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  const selected = Array.isArray(selectedValues) ? selectedValues : [];
  const selectedSet = useMemo(() => new Set(selected.map(String)), [selected]);

  useEffect(() => {
    if (!open) return undefined;

    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false);
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const selectedLabels = options
    .filter((option) => selectedSet.has(String(option.value)))
    .map((option) => option.label);
  const displayValue = selectedLabels.length === 0
    ? placeholder
    : selectedLabels.length === 1 ? selectedLabels[0] : `${selectedLabels.length} selecionadas`;

  const toggleValue = useCallback((value) => {
    const normalizedValue = String(value);
    const next = selectedSet.has(normalizedValue)
      ? selected.filter((item) => String(item) !== normalizedValue)
      : [...selected, value];
    onChange(next);
  }, [selected, selectedSet, onChange]);

  return (
    <div className="teacher-mocks-create-advanced-filter" ref={wrapperRef}>
      <label>{label}</label>
      <button type="button" className={open ? "open" : undefined} onClick={() => setOpen((current) => !current)}>
        <span className={selectedLabels.length ? "" : "placeholder"}>{displayValue}</span>
        <ChevronDown size={13} color={open ? "#0369a1" : "#94a3b8"} aria-hidden="true" />
      </button>
      {open && (
        <div className="teacher-mocks-create-advanced-menu">
          {isLoading && <div className="teacher-mocks-create-advanced-option muted">Carregando...</div>}
          {!isLoading && options.length === 0 && (
            <div className="teacher-mocks-create-advanced-option muted">Nenhuma opção</div>
          )}
          {!isLoading && options.map((option) => {
            const active = selectedSet.has(String(option.value));
            return (
              <button
                key={option.value}
                type="button"
                className={`teacher-mocks-create-advanced-option${active ? " active" : ""}`}
                onClick={() => toggleValue(option.value)}
              >
                <span>{option.label}</span>
                {active && <Check size={13} color="#0369a1" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function CompactSingleSelectDropdown({ label, value, onChange, options, placeholder = "Qualquer", disabled = false, onBlur }) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false);
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const selectedOption = options.find((option) => option.value === value);
  const displayValue = selectedOption ? selectedOption.label : placeholder;

  const handleSelect = (newValue) => {
    onChange(newValue);
    setOpen(false);
  };

  return (
    <div className="teacher-mocks-create-advanced-filter" ref={wrapperRef}>
      {label && <label>{label}</label>}
      <button type="button" className={open ? "open" : undefined} onClick={() => setOpen((current) => !current)} onBlur={onBlur} disabled={disabled}>
        <span className={value ? "" : "placeholder"}>{displayValue}</span>
        <ChevronDown size={13} color={open ? "#0369a1" : "#94a3b8"} aria-hidden="true" />
      </button>
      {open && (
        <div className="teacher-mocks-create-advanced-menu teacher-mocks-create-single-menu">
          <button
            type="button"
            className={`teacher-mocks-create-advanced-option${!value ? " active" : ""}`}
            onClick={() => handleSelect("")}
          >
            <span className="teacher-mocks-create-check-slot">
              {!value && <Check size={13} aria-hidden="true" />}
            </span>
            <span>{placeholder}</span>
          </button>
          {options.map((option) => {
            const active = option.value === value;
            return (
              <button
                key={option.value}
                type="button"
                className={`teacher-mocks-create-advanced-option${active ? " active" : ""}`}
                onClick={() => handleSelect(option.value)}
              >
                <span className="teacher-mocks-create-check-slot">
                  {active && <Check size={13} aria-hidden="true" />}
                </span>
                <span>{option.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function MultiSelectDropdown({ label, options, value, onChange, placeholder = "Selecione as opções" }) {
  const selected = Array.isArray(value) ? value : [];
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const toggle = (optionValue) => {
    onChange(
      selected.includes(optionValue)
        ? selected.filter((item) => item !== optionValue)
        : [...selected, optionValue]
    );
  };

  const selectedLabel = selected.length
    ? options
      .filter((option) => selected.includes(option.value))
      .map((option) => option.label)
      .join(", ")
    : placeholder;

  return (
    <div className="teacher-mocks-manager-field" ref={wrapperRef}>
      <label>{label}</label>
      <div className="teacher-mocks-multiselect">
        <button
          type="button"
          className={`teacher-mocks-multiselect-trigger${open ? " open" : ""}`}
          aria-expanded={open}
          onClick={() => setOpen((current) => !current)}
        >
          <span className={selected.length ? "" : "placeholder"}>{selectedLabel}</span>
          <ChevronDown size={14} color={open ? "#0369a1" : undefined} aria-hidden="true" />
        </button>
        {open && (
          <div className="teacher-mocks-multiselect-menu">
            {options.map((option) => (
              <label key={option.value} className="teacher-mocks-multiselect-option">
                <input
                  type="checkbox"
                  checked={selected.includes(option.value)}
                  onChange={() => toggle(option.value)}
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function SelectFilter({ label, value, onChange, options, placeholder = "Selecione" }) {
  return (
    <div className="teacher-mocks-manager-field">
      <label>{label}</label>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </div>
  );
}
